'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import ProjectCard from './ProjectCard';
import ProjectFilter from './ProjectFilter';
import { getAllProjectsSorted } from '@/content/projects';

interface ProjectsInteractiveProps {
  lang?: 'en' | 'de';
}

const ProjectsInteractive = ({ lang = 'en' }: ProjectsInteractiveProps) => {
  const router = useRouter();
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState<'en' | 'de'>(lang);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Load projects from registry
  const projectsData = getAllProjectsSorted();

  useEffect(() => {
    setIsHydrated(true);
    // Update language if prop changes
    setCurrentLanguage(lang);

    const handleLanguageChange = (event: CustomEvent<{language: 'en' | 'de';}>) => {
      setCurrentLanguage(event.detail.language);
    };

    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    return () => {
      window.removeEventListener('languageChange', handleLanguageChange as EventListener);
    };
  }, []);

  const translations = {
    en: {
      heading: 'Projects',
      message: 'Still top secret. Content coming soon!',
      viewCase: 'View Project'
    },
    de: {
      heading: 'Projekte',
      message: 'Noch streng geheim. Inhalte folgen in Kürze!',
      viewCase: 'Projekt ansehen'
    }
  };

  if (!isHydrated) {
    return null;
  }

  // Show content if projects data is available
  if (projectsData && projectsData.length > 0) {
    const filteredProjects = selectedCategory === 'all' 
      ? projectsData 
      : projectsData.filter((project: any) => project.category === selectedCategory);

    const handleViewDetails = (projectSlug: string) => {
      // Don't navigate for placeholder projects
      if (projectSlug === 'project-x-alpha' || projectSlug === 'project-nexus' || 
          projectSlug === 'top-secret-apps' || projectSlug === 'top-secret-games' || 
          projectSlug === 'top-secret-content') {
        return;
      }
      router.push(`/${currentLanguage}/projects-section/${projectSlug}`);
    };

    // Get unique categories from projects
    const uniqueCategories = Array.from(new Set(projectsData.map((p: any) => p.category)));
    const categories = [
      { value: 'all', label: currentLanguage === 'en' ? 'All' : 'Alle', labelDe: 'Alle' },
      ...uniqueCategories.map((cat: string) => {
        const project = projectsData.find((p: any) => p.category === cat);
        return {
          value: cat,
          label: project?.category || cat,
          labelDe: project?.categoryDe || cat
        };
      })
    ];

    return (
      <section className="relative z-10 min-h-screen px-4 py-20">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-7xl font-bold mb-4 text-accent">
              {translations[currentLanguage].heading}
            </h1>
            <p className="text-xl md:text-2xl text-highlight font-inter">
              {currentLanguage === 'en' ?'Take a look at our innovative tools, experiments, apps and case studies' :'Werfen Sie einen Blick auf unsere innovativen Tools, Experimente, Apps und Fallstudien'
              }
            </p>
          </div>
          {categories.length > 1 && (
            <ProjectFilter
              categories={categories}
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
              currentLanguage={currentLanguage}
            />
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8">
            {filteredProjects.map((project: any, index: number) => (
              <ProjectCard
                key={project.id}
                project={project}
                currentLanguage={currentLanguage}
                onViewDetails={() => handleViewDetails(project.slug)}
                index={index}
                translations={{ viewCase: translations[currentLanguage].viewCase }}
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Show coming soon message if no data
  return (
    <section className="relative z-10 min-h-screen flex items-center justify-center px-4 py-20">
      <div className="text-center">
        <h1 className="text-5xl md:text-7xl font-bold mb-4 text-accent">
          {translations[currentLanguage].heading}
        </h1>
        <p className="text-xl md:text-2xl text-highlight font-inter mb-6">
          {currentLanguage === 'en' ?'Take a look at our innovative tools, experiments, apps and case studies' :'Werfen Sie einen Blick auf unsere innovativen Tools, Experimente, Apps und Fallstudien'
          }
        </p>
        <p className="text-xl md:text-2xl text-gray-300">
          {translations[currentLanguage].message}
        </p>
      </div>
    </section>
  );
};

export default ProjectsInteractive;